import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  GlobeAltIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  CheckCircleIcon,
  ClockIcon,
  UsersIcon,
  ShieldCheckIcon,
  XMarkIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  NoSymbolIcon,
} from '../../icons';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { adminAPI } from '../../services/adminAPI';
import ConfirmDialog from '../../components/admin/ConfirmDialog';
import OneTimePasswordModal from '../../components/admin/OneTimePasswordModal';
import { COURT_ADMIN_ASSIGNABLE_ROLES, getRoleLabel } from '../../utils/adminRoles';
import { formatDateTime } from '../../utils/transcriptionMeta';

const TABS = [
  { id: 'languages', label: 'Languages', icon: GlobeAltIcon },
  { id: 'users', label: 'User & Role Management', icon: UsersIcon },
];

const LANGUAGE_SEED = [
  { id: 'en', name: 'English', native: 'English', code: 'en', enabled: true, isDefault: true, addedOn: '12 Jan 2024', updatedOn: '02 Mar 2026' },
  { id: 'tw', name: 'Twi', native: 'Twi', code: 'tw', enabled: true, isDefault: false, addedOn: '12 Jan 2024', updatedOn: '28 Feb 2026' },
  { id: 'ee', name: 'Ewe', native: 'Eʋegbe', code: 'ee', enabled: true, isDefault: false, addedOn: '15 Mar 2024', updatedOn: '12 Feb 2026' },
  { id: 'ha', name: 'Hausa', native: 'Hausa', code: 'ha', enabled: true, isDefault: false, addedOn: '02 Apr 2024', updatedOn: '05 Mar 2026' },
  { id: 'ga', name: 'Ga', native: 'Ga', code: 'ga', enabled: true, isDefault: false, addedOn: '15 Mar 2024', updatedOn: '20 Feb 2026' },
  { id: 'fr', name: 'French', native: 'Français', code: 'fr', enabled: true, isDefault: false, addedOn: '12 Jan 2024', updatedOn: '18 Feb 2026' },
  { id: 'zh', name: 'Chinese', native: '中文', code: 'zh', enabled: true, isDefault: false, addedOn: '20 Feb 2024', updatedOn: '01 Mar 2026' },
  // { id: 'dag', name: 'Dagbani', native: 'Dagbani', code: 'dag', enabled: false, isDefault: false, addedOn: '02 Apr 2024', updatedOn: '05 Mar 2026' },
  // { id: 'fat', name: 'Fante', native: 'Fante', code: 'fat', enabled: false, isDefault: false, addedOn: '02 Apr 2024', updatedOn: '05 Mar 2026' },
];

const COURT_ROLE_SEED = [
  {
    id: 'role-court_admin',
    name: 'Court Admin',
    key: 'court_admin',
    description: 'Administer this court tenant',
    permissions: ['Manage users', 'Tenant setups', 'Approvals', 'Audit log'],
    usersCount: 1,
    createdOn: '12 Jan 2024',
  },
  {
    id: 'role-registrar',
    name: 'Registrar',
    key: 'registrar',
    description: 'Capture and manage court sessions',
    permissions: ['Record', 'Upload', 'Edit drafts', 'Export'],
    usersCount: 2,
    createdOn: '12 Jan 2024',
  },
  {
    id: 'role-judge',
    name: 'Judge',
    key: 'judge',
    description: 'Review and certify transcripts',
    permissions: ['Review', 'Certify', 'Export'],
    usersCount: 1,
    createdOn: '18 Feb 2024',
  },
  {
    id: 'role-reviewer',
    name: 'Reviewer',
    key: 'reviewer',
    description: 'Review draft transcripts',
    permissions: ['Review', 'Comment', 'Export'],
    usersCount: 1,
    createdOn: '02 Apr 2024',
  },
];

const NestedCard = ({ title, subtitle, children, className = '', actions }) => (
  <div className={`md-card border border-matdash-border shadow-none h-full ${className}`.trim()}>
    <div className="md-card-body p-5">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          {title && <h4 className="text-base font-semibold text-matdash-dark mb-1">{title}</h4>}
          {subtitle && <p className="md-card-subtitle mb-0">{subtitle}</p>}
        </div>
        {actions}
      </div>
      {children}
    </div>
  </div>
);

const ToggleSwitch = ({ checked, onChange, id }) => (
  <button
    type="button"
    id={id}
    role="switch"
    aria-checked={checked}
    onClick={() => onChange(!checked)}
    className={`md-switch ${checked ? 'md-switch-track-on' : 'md-switch-track-off'}`}
  >
    <span className={`md-switch-thumb ${checked ? 'translate-x-4' : 'translate-x-0'}`} />
  </button>
);

const KpiMini = ({ icon: Icon, color, bg, label, value, hint }) => (
  <div className="md-card border border-matdash-border shadow-none">
    <div className="md-card-body p-4 flex items-center gap-3">
      <div className="w-11 h-11 rounded-full flex items-center justify-center shrink-0" style={{ background: bg }}>
        <Icon className="h-5 w-5" style={{ color }} />
      </div>
      <div>
        <p className="text-xs font-medium text-matdash-muted mb-0">{label}</p>
        <p className="text-xl font-bold text-matdash-dark mb-0 leading-tight">{value}</p>
        {hint && <p className="text-xs text-matdash-muted mb-0">{hint}</p>}
      </div>
    </div>
  </div>
);

const ActionBtn = ({ label, onClick, dark, danger, children }) => (
  <button
    type="button"
    title={label}
    aria-label={label}
    onClick={onClick}
    className={`w-8 h-8 inline-flex items-center justify-center rounded-lg border transition-colors ${
      danger
        ? dark
          ? 'border-gray-700 text-red-400 hover:bg-gray-800'
          : 'border-gray-200 text-matdash-danger hover:bg-matdash-danger-subtle'
        : dark
          ? 'border-gray-700 text-gray-300 hover:bg-gray-800'
          : 'border-gray-200 text-matdash-muted hover:bg-matdash-primary-subtle hover:text-matdash-primary'
    }`}
  >
    {children}
  </button>
);

const AdminSettings = () => {
  const { user, logout, updateUser } = useAuth();
  const { dark } = useTheme();
  const tenantId = user?.tenant_id || null;
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const activeTab = TABS.some((t) => t.id === tabParam) ? tabParam : 'languages';

  const [languages, setLanguages] = useState(LANGUAGE_SEED);
  const [langQuery, setLangQuery] = useState('');
  const [langStatus, setLangStatus] = useState('all');
  const [roles, setRoles] = useState(COURT_ROLE_SEED);
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [showCreateRole, setShowCreateRole] = useState(false);
  const [showAddUser, setShowAddUser] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [createdCredentials, setCreatedCredentials] = useState(null);
  const [roleForm, setRoleForm] = useState({ name: '', description: '' });
  const [userForm, setUserForm] = useState({ first_name: '', last_name: '', email: '', role: 'registrar' });
  const [deleteRoleTarget, setDeleteRoleTarget] = useState(null);
  const [deleteUserTarget, setDeleteUserTarget] = useState(null);
  const [viewUser, setViewUser] = useState(null);
  const [toggleActiveTarget, setToggleActiveTarget] = useState(null);
  const [saving, setSaving] = useState(false);

  const setTab = (id) => {
    setSearchParams(id === 'languages' ? {} : { tab: id }, { replace: true });
  };

  const loadUsers = useCallback(async () => {
    if (!tenantId) return;
    setUsersLoading(true);
    try {
      const resp = await adminAPI.getUsers(tenantId);
      setUsers(resp.data?.data || resp.data || []);
    } finally {
      setUsersLoading(false);
    }
  }, [tenantId]);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const filteredLanguages = useMemo(() => {
    return languages.filter((lang) => {
      const q = langQuery.trim().toLowerCase();
      const matchesQuery = !q
        || lang.name.toLowerCase().includes(q)
        || lang.native.toLowerCase().includes(q)
        || lang.code.toLowerCase().includes(q);
      const matchesStatus = langStatus === 'all'
        || (langStatus === 'enabled' && lang.enabled)
        || (langStatus === 'disabled' && !lang.enabled);
      return matchesQuery && matchesStatus;
    });
  }, [languages, langQuery, langStatus]);

  const enabledCount = languages.filter((l) => l.enabled).length;
  const disabledCount = languages.length - enabledCount;
  const activeUsers = users.filter((u) => u.is_active).length;

  const toggleLanguage = (id) => {
    setLanguages((prev) => prev.map((l) => (l.id === id ? { ...l, enabled: !l.enabled } : l)));
    toast.success('Language status updated for this court');
  };

  const openCreateRole = () => {
    setEditingRole(null);
    setRoleForm({ name: '', description: '' });
    setShowCreateRole(true);
  };

  const openEditRole = (role) => {
    setEditingRole(role);
    setRoleForm({ name: role.name || '', description: role.description || '' });
    setShowCreateRole(true);
  };

  const closeRoleModal = () => {
    setShowCreateRole(false);
    setEditingRole(null);
    setRoleForm({ name: '', description: '' });
  };

  const openAddUser = () => {
    setEditingUser(null);
    setUserForm({ first_name: '', last_name: '', email: '', role: 'registrar' });
    setShowAddUser(true);
  };

  const openEditUser = (row) => {
    setEditingUser(row);
    setUserForm({
      first_name: row.first_name || '',
      last_name: row.last_name || '',
      email: row.email || '',
      role: row.role || 'registrar',
    });
    setShowAddUser(true);
  };

  const closeUserModal = () => {
    setShowAddUser(false);
    setEditingUser(null);
    setUserForm({ first_name: '', last_name: '', email: '', role: 'registrar' });
  };

  const handleCreateRole = (e) => {
    e.preventDefault();
    const name = roleForm.name.trim();
    const description = roleForm.description.trim() || 'Custom court role';
    if (editingRole) {
      setRoles((prev) =>
        prev.map((r) =>
          r.id === editingRole.id
            ? {
                ...r,
                name,
                description,
                key: name.toLowerCase().replace(/\s+/g, '_'),
              }
            : r
        )
      );
      toast.success(`Role “${name}” updated`);
    } else {
      const today = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
      const key = name.toLowerCase().replace(/\s+/g, '_');
      setRoles((prev) => [
        ...prev,
        {
          id: `role-${Date.now()}`,
          name,
          key,
          description,
          permissions: [],
          usersCount: 0,
          createdOn: today,
        },
      ]);
      toast.success(`Role “${name}” created`);
    }
    closeRoleModal();
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingUser) {
        await adminAPI.updateUser(editingUser.id, userForm);
        toast.success('User updated');
        closeUserModal();
        loadUsers();
      } else {
        const resp = await adminAPI.createUser(tenantId, userForm);
        const created = resp.data?.data || resp.data || {};
        closeUserModal();
        if (created.temporary_password) {
          setCreatedCredentials(created);
        } else {
          toast.success('User created');
        }
        loadUsers();
      }
    } catch (err) {
      toast.error(err.response?.data?.error || err.response?.data?.message || (editingUser ? 'Failed to update user' : 'Failed to create user'));
    } finally {
      setSaving(false);
    }
  };

  const handleToggleUserActive = async () => {
    if (!toggleActiveTarget) return;
    const row = toggleActiveTarget;
    setSaving(true);
    try {
      const nextActive = !row.is_active;
      await adminAPI.updateUser(row.id, { is_active: nextActive });
      setToggleActiveTarget(null);
      if (!nextActive && row.id === user?.id) {
        toast.error('Your account has been deactivated.');
        updateUser({ ...user, is_active: false });
        logout();
        return;
      }
      toast.success(nextActive ? 'User activated' : 'User deactivated');
      loadUsers();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update user');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteRole = () => {
    if (!deleteRoleTarget) return;
    setRoles((prev) => prev.filter((r) => r.id !== deleteRoleTarget.id));
    toast.success(`Role “${deleteRoleTarget.name}” deleted`);
    setDeleteRoleTarget(null);
  };

  const handleDeleteUser = async () => {
    if (!deleteUserTarget) return;
    setSaving(true);
    try {
      await adminAPI.updateUser(deleteUserTarget.id, { is_active: false });
      toast.success('User removed');
      setDeleteUserTarget(null);
      loadUsers();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to remove user');
    } finally {
      setSaving(false);
    }
  };

  const inputCls = `w-full px-3 py-2.5 text-sm rounded-lg border focus:outline-none focus:ring-2 focus:ring-matdash-primary/40
    ${dark ? 'bg-gray-800 border-gray-700 text-gray-100 placeholder-gray-500' : 'bg-white border-gray-200 text-gray-900'}`;

  return (
    <>
      <div className={`md-card overflow-hidden ${dark ? '!bg-gray-900 !border-gray-800' : ''}`}>
        <div className="flex flex-col md:flex-row min-h-[560px]">
          <nav
            className={`md:w-56 lg:w-64 shrink-0 border-b md:border-b-0 md:border-r p-3 space-y-1
              ${dark ? 'border-gray-800 bg-gray-900' : 'border-matdash-border bg-gray-50/60'}`}
            role="tablist"
            aria-orientation="vertical"
          >
            {TABS.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => setTab(tab.id)}
                  className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium text-left transition-colors
                    ${isActive
                      ? 'bg-matdash-primary text-white shadow-sm'
                      : dark
                        ? 'text-gray-300 hover:bg-gray-800'
                        : 'text-matdash-muted hover:bg-white hover:text-matdash-dark'}`}
                >
                  <Icon className="h-5 w-5 shrink-0" />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </nav>

          <div className="flex-1 min-w-0 p-5">
            {activeTab === 'languages' && (
              <div className="space-y-4">
                <div className="md-alert md-alert-success flex items-center gap-2">
                  <CheckCircleIcon className="h-4 w-4 shrink-0" />
                  Language defaults apply to new sessions in this court only.
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                  <KpiMini icon={GlobeAltIcon} bg="#FDF0E6" color="#bf5d09" label="Total Languages" value={languages.length} hint="Available to enable" />
                  <KpiMini icon={CheckCircleIcon} bg="#E6FFFA" color="#13DEB9" label="Enabled Languages" value={enabledCount} hint="Active for this court" />
                  <KpiMini icon={ClockIcon} bg="#FFF6E5" color="#FFAE1F" label="Disabled Languages" value={disabledCount} hint="Currently disabled" />
                </div>

                <NestedCard title="Court Languages" subtitle="Manage default transcription languages for sessions in this court.">
                  <div className="flex flex-wrap items-center gap-2 mb-4">
                    <div className="relative flex-1 min-w-[180px] max-w-xs">
                      <MagnifyingGlassIcon className="h-4 w-4 text-matdash-muted absolute left-3 top-1/2 -translate-y-1/2" />
                      <input
                        value={langQuery}
                        onChange={(e) => setLangQuery(e.target.value)}
                        placeholder="Search languages..."
                        className="md-form-control pl-9"
                      />
                    </div>
                    <select
                      value={langStatus}
                      onChange={(e) => setLangStatus(e.target.value)}
                      className="md-form-select max-w-[160px]"
                    >
                      <option value="all">All Status</option>
                      <option value="enabled">Enabled</option>
                      <option value="disabled">Disabled</option>
                    </select>
                  </div>

                  <div className="overflow-x-auto -mx-1">
                    <table className="md-table min-w-full">
                      <thead>
                        <tr>
                          <th>Language</th>
                          <th>Native Name</th>
                          <th>Code</th>
                          <th>Status</th>
                          <th>Default</th>
                          <th>Last Updated</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredLanguages.map((lang) => (
                          <tr key={lang.id}>
                            <td><span className="font-medium text-matdash-dark">{lang.name}</span></td>
                            <td className="text-matdash-muted">{lang.native}</td>
                            <td>
                              <span className="inline-flex px-2 py-0.5 rounded bg-matdash-body text-matdash-muted text-xs font-medium">
                                {lang.code}
                              </span>
                            </td>
                            <td>
                              <ToggleSwitch
                                id={`ct-lang-${lang.id}`}
                                checked={lang.enabled}
                                onChange={() => toggleLanguage(lang.id)}
                              />
                            </td>
                            <td>
                              {lang.isDefault ? (
                                <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700">Default</span>
                              ) : (
                                <span className="text-matdash-muted text-xs">—</span>
                              )}
                            </td>
                            <td className="text-matdash-muted text-xs">{lang.updatedOn}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </NestedCard>
              </div>
            )}

            {activeTab === 'users' && (
              <div className="space-y-4">
                <NestedCard
                  title="Court roles"
                  subtitle="Roles available for staff in this court."
                  actions={
                    <button
                      type="button"
                      onClick={openCreateRole}
                      className="md-btn md-btn-primary md-btn-sm inline-flex items-center gap-1"
                    >
                      <PlusIcon className="h-4 w-4" />
                      Create Role
                    </button>
                  }
                >
                  <div className="overflow-x-auto -mx-1">
                    <table className="md-table min-w-full">
                      <thead>
                        <tr>
                          <th>Role</th>
                          <th>Description</th>
                          <th>Users</th>
                          <th>Created On</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {roles.map((r) => (
                          <tr key={r.id}>
                            <td><span className="font-medium text-matdash-dark">{r.name}</span></td>
                            <td className="text-matdash-muted text-sm max-w-xs">{r.description}</td>
                            <td className="text-matdash-muted text-sm">{r.usersCount}</td>
                            <td className="text-matdash-muted text-xs">{r.createdOn}</td>
                            <td>
                              <div className="flex items-center gap-1.5">
                                {/* <ActionBtn label="View role" dark={dark} onClick={() => toast.message(r.name, { description: r.description })}>
                                  <EyeIcon className="h-4 w-4" />
                                </ActionBtn>
                                <ActionBtn label="Edit role" dark={dark} onClick={() => openEditRole(r)}>
                                  <PencilIcon className="h-4 w-4" />
                                </ActionBtn> */}
                                <ActionBtn label="Delete role" dark={dark} danger onClick={() => setDeleteRoleTarget(r)}>
                                  <TrashIcon className="h-4 w-4" />
                                </ActionBtn>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </NestedCard>

                <NestedCard
                  title="Court Users"
                  subtitle={`${activeUsers} active staff assigned to this court`}
                  actions={
                    <button
                      type="button"
                      onClick={openAddUser}
                      className="md-btn md-btn-primary md-btn-sm inline-flex items-center gap-1"
                    >
                      <PlusIcon className="h-4 w-4" />
                      Add User
                    </button>
                  }
                >
                  <div className="overflow-x-auto -mx-1">
                    <table className="md-table min-w-full">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Email</th>
                          <th>Role</th>
                          <th>Status</th>
                          <th>Last login</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {usersLoading && (
                          <tr>
                            <td colSpan={6} className="text-center text-matdash-muted py-8">Loading users…</td>
                          </tr>
                        )}
                        {!usersLoading && users.map((u) => (
                          <tr key={u.id}>
                            <td className="font-medium text-matdash-dark">{u.first_name} {u.last_name}</td>
                            <td className="text-matdash-muted text-sm">{u.email}</td>
                            <td className="text-matdash-muted text-sm">
                              {roles.find((r) => r.key === u.role)?.name || getRoleLabel(u.role)}
                            </td>
                            <td>
                              <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                                u.is_active ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'
                              }`}>
                                {u.is_active ? 'Active' : 'Inactive'}
                              </span>
                            </td>
                            <td className="text-matdash-muted text-xs">
                              {u.last_login_at ? formatDateTime(u.last_login_at) : '—'}
                            </td>
                            <td>
                              <div className="flex items-center gap-1.5">
                                <ActionBtn
                                  label="View user"
                                  dark={dark}
                                  onClick={() => setViewUser(u)}
                                >
                                  <EyeIcon className="h-4 w-4" />
                                </ActionBtn>
                                <ActionBtn
                                  label="Edit user"
                                  dark={dark}
                                  onClick={() => openEditUser(u)}
                                >
                                  <PencilIcon className="h-4 w-4" />
                                </ActionBtn>
                                <ActionBtn
                                  label={u.is_active ? 'Deactivate user' : 'Activate user'}
                                  dark={dark}
                                  onClick={() => setToggleActiveTarget(u)}
                                >
                                  {u.is_active
                                    ? <NoSymbolIcon className="h-4 w-4" />
                                    : <CheckCircleIcon className="h-4 w-4 text-emerald-600" />}
                                </ActionBtn>
                                <ActionBtn label="Delete user" dark={dark} danger onClick={() => setDeleteUserTarget(u)}>
                                  <TrashIcon className="h-4 w-4" />
                                </ActionBtn>
                              </div>
                            </td>
                          </tr>
                        ))}
                        {!usersLoading && users.length === 0 && (
                          <tr>
                            <td colSpan={6} className="text-center text-matdash-muted py-8">No users yet.</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </NestedCard>
              </div>
            )}
          </div>
        </div>
      </div>

      {showCreateRole && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <form onSubmit={handleCreateRole} className={`w-full max-w-lg rounded-2xl shadow-xl ${dark ? 'bg-gray-900' : 'bg-white'}`}>
            <div className={`flex items-start justify-between px-6 py-5 border-b ${dark ? 'border-gray-800' : 'border-matdash-border'}`}>
              <div className="flex items-start gap-3">
                <div className="w-11 h-11 rounded-full bg-matdash-primary flex items-center justify-center flex-shrink-0">
                  <ShieldCheckIcon className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h2 className={`text-lg font-bold mb-0 ${dark ? 'text-gray-50' : 'text-matdash-dark'}`}>
                    {editingRole ? 'Edit role' : 'Create Role'}
                  </h2>
                  <p className={`text-sm mt-0.5 ${dark ? 'text-gray-400' : 'text-matdash-muted'}`}>
                    {editingRole ? 'Update this court role.' : 'Define a new court role.'}
                  </p>
                </div>
              </div>
              <button type="button" onClick={closeRoleModal} className="text-matdash-muted p-1" aria-label="Close">
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            <div className="px-6 py-5 space-y-4">
              <div>
                <label className="block text-sm font-semibold text-matdash-dark mb-1">Role Name <span className="text-red-500">*</span></label>
                <input required value={roleForm.name} onChange={(e) => setRoleForm({ ...roleForm, name: e.target.value })} className={inputCls} placeholder="e.g. Stenographer" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-matdash-dark mb-1">Description</label>
                <textarea rows={3} value={roleForm.description} onChange={(e) => setRoleForm({ ...roleForm, description: e.target.value })} className={inputCls} placeholder="What this role is responsible for" />
              </div>
            </div>
            <div className={`flex justify-end gap-2 px-6 py-4 border-t ${dark ? 'border-gray-800' : 'border-matdash-border'}`}>
              <button type="button" onClick={closeRoleModal} className="md-btn md-btn-outline md-btn-sm">Cancel</button>
              <button type="submit" className="md-btn md-btn-primary md-btn-sm inline-flex items-center gap-1.5">
                {!editingRole && <PlusIcon className="h-4 w-4" />}
                {editingRole ? 'Save changes' : 'Create Role'}
              </button>
            </div>
          </form>
        </div>
      )}

      {showAddUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <form onSubmit={handleCreateUser} className={`w-full max-w-lg rounded-2xl shadow-xl ${dark ? 'bg-gray-900' : 'bg-white'}`}>
            <div className={`flex items-start justify-between px-6 py-5 border-b ${dark ? 'border-gray-800' : 'border-matdash-border'}`}>
              <div className="flex items-start gap-3">
                <div className="w-11 h-11 rounded-full bg-matdash-primary flex items-center justify-center flex-shrink-0">
                  <UsersIcon className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h2 className={`text-lg font-bold mb-0 ${dark ? 'text-gray-50' : 'text-matdash-dark'}`}>
                    {editingUser ? 'Edit User' : 'Add User'}
                  </h2>
                  <p className={`text-sm mt-0.5 ${dark ? 'text-gray-400' : 'text-matdash-muted'}`}>
                    {editingUser ? 'Update this court user.' : 'Provision a user for this court.'}
                  </p>
                </div>
              </div>
              <button type="button" onClick={closeUserModal} className="text-matdash-muted p-1" aria-label="Close">
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            <div className="px-6 py-5 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-semibold text-matdash-dark mb-1">First name</label>
                  <input required value={userForm.first_name} onChange={(e) => setUserForm({ ...userForm, first_name: e.target.value })} className={inputCls} />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-matdash-dark mb-1">Last name</label>
                  <input required value={userForm.last_name} onChange={(e) => setUserForm({ ...userForm, last_name: e.target.value })} className={inputCls} />
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-matdash-dark mb-1">Email</label>
                <input required type="email" value={userForm.email} onChange={(e) => setUserForm({ ...userForm, email: e.target.value })} className={inputCls} disabled={Boolean(editingUser)} />
              </div>
              <div>
                <label className="block text-sm font-semibold text-matdash-dark mb-1">Role</label>
                <select value={userForm.role} onChange={(e) => setUserForm({ ...userForm, role: e.target.value })} className={inputCls}>
                  {COURT_ADMIN_ASSIGNABLE_ROLES.map((r) => (
                    <option key={r.value} value={r.value}>{r.label}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className={`flex justify-end gap-2 px-6 py-4 border-t ${dark ? 'border-gray-800' : 'border-matdash-border'}`}>
              <button type="button" onClick={closeUserModal} className="md-btn md-btn-outline md-btn-sm">Cancel</button>
              <button type="submit" disabled={saving} className="md-btn md-btn-primary md-btn-sm">
                {saving
                  ? (editingUser ? 'Saving…' : 'Creating…')
                  : (editingUser ? 'Save changes' : 'Create user')}
              </button>
            </div>
          </form>
        </div>
      )}

      <ConfirmDialog
        open={Boolean(deleteRoleTarget)}
        title="Delete role"
        message={`Delete role “${deleteRoleTarget?.name}”? This cannot be undone.`}
        confirmLabel="Delete"
        onConfirm={handleDeleteRole}
        onCancel={() => setDeleteRoleTarget(null)}
        danger
      />

      <ConfirmDialog
        open={Boolean(deleteUserTarget)}
        title="Delete user"
        message={`Remove ${deleteUserTarget?.first_name} ${deleteUserTarget?.last_name}? They will lose access immediately.`}
        confirmLabel="Delete"
        onConfirm={handleDeleteUser}
        onCancel={() => setDeleteUserTarget(null)}
        danger
        loading={saving}
      />

      <ConfirmDialog
        open={Boolean(toggleActiveTarget)}
        title={toggleActiveTarget?.is_active ? 'Deactivate user' : 'Activate user'}
        message={
          toggleActiveTarget?.is_active
            ? `Deactivate ${toggleActiveTarget?.first_name} ${toggleActiveTarget?.last_name}? They will lose access until reactivated.`
            : `Activate ${toggleActiveTarget?.first_name} ${toggleActiveTarget?.last_name}? They will regain access to this court.`
        }
        confirmLabel={toggleActiveTarget?.is_active ? 'Deactivate' : 'Activate'}
        onConfirm={handleToggleUserActive}
        onCancel={() => setToggleActiveTarget(null)}
        danger={Boolean(toggleActiveTarget?.is_active)}
        loading={saving}
      />

      {viewUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setViewUser(null)}>
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="view-user-title"
            className={`w-full max-w-lg rounded-2xl shadow-xl ${dark ? 'bg-gray-900' : 'bg-white'}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={`flex items-start justify-between px-6 py-5 border-b ${dark ? 'border-gray-800' : 'border-matdash-border'}`}>
              <div className="flex items-start gap-3">
                <div className="w-11 h-11 rounded-full bg-matdash-primary flex items-center justify-center flex-shrink-0">
                  <EyeIcon className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h2 id="view-user-title" className={`text-lg font-bold mb-0 ${dark ? 'text-gray-50' : 'text-matdash-dark'}`}>
                    {viewUser.first_name} {viewUser.last_name}
                  </h2>
                  <p className={`text-sm mt-0.5 ${dark ? 'text-gray-400' : 'text-matdash-muted'}`}>
                    Court user details
                  </p>
                </div>
              </div>
              <button type="button" onClick={() => setViewUser(null)} className="text-matdash-muted p-1" aria-label="Close">
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            <dl className="px-6 py-5 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4 text-sm">
              <div>
                <dt className="text-matdash-muted">Email</dt>
                <dd className="text-matdash-dark mt-0.5 break-all">{viewUser.email || '—'}</dd>
              </div>
              <div>
                <dt className="text-matdash-muted">Phone</dt>
                <dd className="text-matdash-dark mt-0.5">{viewUser.phone || '—'}</dd>
              </div>
              <div>
                <dt className="text-matdash-muted">Role</dt>
                <dd className="text-matdash-dark mt-0.5">
                  {roles.find((r) => r.key === viewUser.role)?.name || getRoleLabel(viewUser.role)}
                </dd>
              </div>
              <div>
                <dt className="text-matdash-muted">Status</dt>
                <dd className="mt-0.5">
                  <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                    viewUser.is_active ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'
                  }`}>
                    {viewUser.is_active ? 'Active' : 'Inactive'}
                  </span>
                </dd>
              </div>
              <div>
                <dt className="text-matdash-muted">Last login</dt>
                <dd className="text-matdash-dark mt-0.5">
                  {viewUser.last_login_at ? formatDateTime(viewUser.last_login_at) : '—'}
                </dd>
              </div>
              <div>
                <dt className="text-matdash-muted">Created</dt>
                <dd className="text-matdash-dark mt-0.5">
                  {viewUser.created_at ? formatDateTime(viewUser.created_at) : '—'}
                </dd>
              </div>
              {viewUser.deactivated_at && (
                <div>
                  <dt className="text-matdash-muted">Deactivated</dt>
                  <dd className="text-matdash-dark mt-0.5">{formatDateTime(viewUser.deactivated_at)}</dd>
                </div>
              )}
            </dl>
            <div className={`flex justify-end px-6 py-4 border-t ${dark ? 'border-gray-800' : 'border-matdash-border'}`}>
              <button type="button" onClick={() => setViewUser(null)} className="md-btn md-btn-outline md-btn-sm">Close</button>
            </div>
          </div>
        </div>
      )}

      <OneTimePasswordModal
        open={Boolean(createdCredentials)}
        credentials={createdCredentials}
        dark={dark}
        onClose={() => setCreatedCredentials(null)}
      />
    </>
  );
};

export default AdminSettings;
